import * as React from 'react';

import classNames from 'classnames';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import { getConfig, URLS, LEFT_MENU_ID } from '@constants';

import { EMainMenuCounters, ERefsLeftMenu } from '@enums';

import { IMainMenuAppsStoreParams } from '@stores';

import { counters, getTranslation, translations } from '@base/utils';

import {
	MainMenuAppsSet, MainMenuContentInfo, MainMenuItem,
	MainMenuSet, MainMenuUser, MainMenuAuth,
} from '@components';

import {
	IconCommunities,
	IconFeed,
	IconFriends,
	IconFull,
	IconGames,
	IconGifts,
	IconMusic,
	IconPhoto,
	IconSignOut,
	IconVideo,
} from '@icons';

import { getSectionLink } from '../utils';

import { IUser } from '@interfaces';

import s from './MainMenuView.scss';

export interface IMainMenuViewProps {
	isVisible: boolean;
	currentUser: IUser;
	menuApps: IMainMenuAppsStoreParams;
	className?: string;
	mainMenuViewRef: React.RefObject<HTMLDivElement>;
	onLogOutClick?(event: React.MouseEvent<HTMLAnchorElement>): void;
	onLogInClick?(event: React.MouseEvent<HTMLAnchorElement>): void;
	onRegistrationClick?(event: React.MouseEvent<HTMLAnchorElement>): void;
	onHide(): void;
	onLeftMenuLoad(): void;
}

export const MainMenuView = ({
	className,
	onLogOutClick,
	onLogInClick,
	onRegistrationClick,
	onLeftMenuLoad,
	currentUser,
	menuApps: {
		myApps,
		suggestApps,
	},
	isVisible,
	onHide,
	mainMenuViewRef,
}: IMainMenuViewProps) => {
	const t = getTranslation('components.MainMenu');
	const {
		avatar,
		name,
		isMyCreated,
		dir,
	} = currentUser;
	const { apps, hosts, isMobile, links, contentInfo } = getConfig();
	const appsCatalogUrl = apps?.pathname || URLS.APPS;
	const menuClassName = classNames(
		className,
		s.block,
		{ [s.visible]: isVisible },
	);

	const onMenuItemClick = () => {
		onHide();
	};

	return <div
		className={menuClassName}
		ref={mainMenuViewRef}
	>
		{/* eslint-disable @typescript-eslint/ban-ts-comment */}
		{/* @ts-ignore */}
		<LazyLoadComponent
			visibleByDefault={!isMobile}
			afterLoad={onLeftMenuLoad}
		>
			<div
				id={LEFT_MENU_ID}
				className={s.wrapper}
			>
				{isMyCreated
					? <MainMenuUser
						avatar={avatar?.url?.size45}
						link={dir || ''}
						name={name?.full}
						onClick={onHide}
					/>
					: <>
						<MainMenuAuth
							loginUrl={links.login}
							registrationUrl={links.registration}
						/>
						<div className={s.divider} />
					</>}

				<MainMenuSet>
					{isMyCreated &&
						<>
							{ /* Лента */}
							<MainMenuItem
								title={t('feed')}
								onClick={onMenuItemClick}
								link={getSectionLink(URLS.MAIN)}
								icon={IconFeed}
								counter={EMainMenuCounters.FEED}
							/>
							{ /* Друзья */}
							<MainMenuItem
								title={t('friends')}
								onClick={onMenuItemClick}
								link={getSectionLink(URLS.FRIENDS)}
								icon={IconFriends}
								counter={EMainMenuCounters.FRIENDS}
							/>
							{ /* Фото */ }
							<MainMenuItem
								title={t('photo')}
								onClick={onMenuItemClick}
								link={getSectionLink(URLS.MOBWEB_PHOTO)}
								icon={IconPhoto}
							/>
						</>}
					{ /* Видео */}
					<MainMenuItem
						title={t('video')}
						onClick={onMenuItemClick}
						link={getSectionLink(URLS.VIDEO)}
						icon={IconVideo}
						counter={EMainMenuCounters.VIDEO}
					/>
					{ /* Музыка */}
					<MainMenuItem
						title={t('music')}
						onClick={onMenuItemClick}
						link={getSectionLink(URLS.MUSIC)}
						icon={IconMusic}
						counter={EMainMenuCounters.MUSIC}
					/>
					{ /* Группы */}
					<MainMenuItem
						title={t('communities')}
						onClick={onMenuItemClick}
						link={getSectionLink(URLS.COMMUNITIES)}
						icon={IconCommunities}
						counter={EMainMenuCounters.COMMUNITIES}
					/>
					{ /* Подарки */ }
					{isMyCreated && <MainMenuItem
						title={t('gifts')}
						onClick={onMenuItemClick}
						link={getSectionLink(URLS.MOBWEB_GIFTS)}
						icon={IconGifts}
					/>}
					{ /* Игры */}
					<MainMenuItem
						title={t('apps')}
						onClick={onMenuItemClick}
						link={appsCatalogUrl}
						icon={IconGames}
						counter={EMainMenuCounters.GAMES_CATALOG}
						isOuterLink
					/>
				</MainMenuSet>

				{ /* Ссылки на "Мои игры" */}
				{isMyCreated && Boolean(myApps?.length) &&
					<>
						<div className={s.divider} />
						<MainMenuAppsSet
							appsArray={myApps}
							title={translations.appCatalog('appsMy')}
							isMyCreated={isMyCreated}
						/>
					</>
				}

				{ /* Ссылки на игры раздела "Рекомендуем" */}
				{isMyCreated && Boolean(suggestApps?.length) &&
					<>
						<div className={s.divider} />
						<MainMenuAppsSet
							appsArray={suggestApps}
							title={translations.common('suggest')}
							isMyCreated={isMyCreated}
							customRef={ERefsLeftMenu.GAMES_PROMO}
						/>
					</>
				}

				<div className={s.divider} />

				{ /* Ссылки на раздел "Полная версия" */}
				<MainMenuSet title={t('other')}>
					<MainMenuItem
						title={t('fullVersion')}
						onClick={onMenuItemClick}
						link={counters.getRbUrl(`${hosts.baseUrlDesktop}/stay_here`, EMainMenuCounters.FULL_VERSION)}
						icon={IconFull}
					/>
					{ /* Выход из профиля */}
					{isMyCreated && <MainMenuItem
						title={t('logout')}
						onClick={onLogOutClick}
						icon={IconSignOut}
						counter={EMainMenuCounters.LOGOUT}
					/>}
				</MainMenuSet>

				<div className={s.divider} />

				{contentInfo.enabled
					? <div className={s.contentInfo}>
						<MainMenuContentInfo contentHtml={contentInfo.html} />
					</div>
					: null}
			</div>
		</LazyLoadComponent>
	</div>;
};
